import { Heading, Text, VStack } from '@ayble-health/dashboard-components';
import { Center, Spinner } from '@chakra-ui/react';
import { useEffect, useRef, useState } from 'react';

import {
  composeApptBlockLayouts,
  formatDateStr,
} from 'components/ProviderHome/utils';
import { MS_MULTIPLIER } from 'components/ProviderHome/constants';
import { HourSlots } from 'components/ProviderHome/HourSlots';
import AppointmentBlock from 'components/ProviderHome/AppointmentBlock';
import useCareMemberAppointments from 'api/dashboard/useCareMemberAppointments';
import { useAuth } from 'hooks';
import AppointmentsColumnHeader from 'components/ProviderHome/AppointmentsColumnHeader';
import CurrentTimeIndicator from 'components/ProviderHome/CurrentTimeIndicator';

export const AppointmentsColumn = ({ memberId }: { memberId?: number }) => {
  const {
    user: { healthie_id },
  } = useAuth();
  const [activeTab, setActiveTab] = useState(0);
  const [now, setNow] = useState(Date.now());
  const nowRef = useRef<HTMLDivElement>(null);
  const firstApptRef = useRef<HTMLDivElement>(null);
  const hasInitialScrolled = useRef(false);

  const todayStartDate = formatDateStr(new Date());
  const tomorrowStartDate = formatDateStr(new Date(Date.now() + 86400000));
  const tomorrowEndDate = formatDateStr(new Date(Date.now() + 86400000 * 2));

  const { appointments: todayAppointments, isLoading: isTodayApptsLoading } =
    useCareMemberAppointments({
      start_date: todayStartDate,
      end_date: tomorrowStartDate,
      healthieId: memberId,
    });
  const {
    appointments: tomorrowAppointments,
    isLoading: isTomorrowApptsLoading,
  } = useCareMemberAppointments({
    start_date: tomorrowStartDate,
    end_date: tomorrowEndDate,
    healthieId: memberId,
  });

  const isLoading =
    activeTab === 0 ? isTodayApptsLoading : isTomorrowApptsLoading;

  const apptLayouts = composeApptBlockLayouts(
    activeTab === 0 ? todayAppointments : tomorrowAppointments,
    now
  );

  useEffect(() => {
    const interval = setInterval(() => setNow(Date.now()), MS_MULTIPLIER);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    if (!hasInitialScrolled.current) {
      if (todayAppointments === undefined) return;
      hasInitialScrolled.current = true;
    }

    if (activeTab === 0) {
      nowRef.current?.scrollIntoView({ block: 'center', behavior: 'smooth' });
    } else {
      firstApptRef.current?.scrollIntoView({
        block: 'center',
        behavior: 'smooth',
      });
    }
  }, [activeTab, todayAppointments]);

  return (
    <VStack w={'100%'} flex={1.25} minH={0}>
      <AppointmentsColumnHeader
        memberId={healthie_id}
        tabs={[
          { label: 'Today', count: todayAppointments?.length },
          { label: 'Tomorrow', count: tomorrowAppointments?.length },
        ]}
        activeTab={activeTab}
        setActiveTab={setActiveTab}
      />

      {!memberId ? (
        <VStack
          flex={1}
          pt={4}
          h={'100%'}
          alignItems={'center'}
          justifyItems={'center'}
        >
          <Text>Select a member to view tasks</Text>
        </VStack>
      ) : isLoading ? (
        <Center h={'100%'}>
          <Spinner color={'subtle'} />
        </Center>
      ) : !apptLayouts?.length ? (
        <VStack
          mx={'auto'}
          align={'center'}
          justify={'center'}
          py={20}
          gap={2}
          maxW={260}
        >
          <Heading
            size={'sm'}
            color={'ink'}
            fontWeight={600}
            textAlign={'center'}
          >
            No appointments scheduled
          </Heading>

          <Text fontSize="sm" textAlign={'center'} color={'subtle'}>
            Enjoy the open time — check back tomorrow for what's coming up.
          </Text>
        </VStack>
      ) : (
        <VStack
          px={8}
          overflowY={'auto'}
          position={'relative'}
          flex={1}
          minH={0}
        >
          <HourSlots />
          {activeTab === 0 && (
            <CurrentTimeIndicator now={now} scrollRef={nowRef} />
          )}

          <VStack position={'absolute'} top={0} left={8} right={8}>
            {apptLayouts.map((apptLayout, i) => {
              return (
                <AppointmentBlock
                  {...apptLayout}
                  scrollRef={i === 0 ? firstApptRef : undefined}
                />
              );
            })}
          </VStack>
        </VStack>
      )}
    </VStack>
  );
};

export default AppointmentsColumn;
